/**
 * useStoresOptions — F3 (DISC-127/130 闭合) 门店下拉数据 hook + 模块级缓存.
 *
 * 背景: v0.10 PR3 frontend 二轮把"选门店"UI 退化为 `<InputNumber>` 手填 store_id
 * (DISC-130), 因当时 backend 无 `GET /api/v1/stores` 列门店清单端点. F3 backend
 * (commit 5ec248b, reviewer ACCEPT-with-followups) 已落 `GET /api/v1/stores`, 本 hook
 * 是 frontend 接力的"单次拉取 + 模块级缓存 + 多调用方复用"层.
 *
 * 设计:
 * - **单次 GET 缓存** (in-memory, 模块级 Promise singleton): 多个表单 mount 同时调用
 *   只触发 1 次 HTTP, 避免 N 个表单 N 次拉. cache key 只按 `includeInactive` 区分
 *   (priv-role 看不可见的下线店属另一份清单, 不混).
 * - **不持久化** (不 localStorage / 不 sessionStorage): 数据规模小 (≤ 几十店),
 *   loginActorChange 时直接 `clear()` 防止跨 actor 漏权; 试点期不引入更重 cache infra.
 * - **错误不缓存**: 失败的 promise 不写 cache, 下一个调用方仍能重试.
 * - **不引 React Query / SWR**: scope 小, 引第三方库不划算; 试点期人工 cache 足够.
 *
 * 调用契约:
 * ```ts
 * const { stores, options, loading, error, reload } = useStoresOptions({ includeInactive: false });
 * // options: Antd Select 直接消费 [{ value: 1, label: 'Ricky 家试点店 (CHS)' }, ...]
 * // stores: 原始 StoreListItem[] 供调用方做 filter (e.g. 排除已绑门店)
 * ```
 *
 * 退出 actor / 切换 actor 时调用方应主动 `clearStoresCache()` 防漏权; 当前实现不强制
 * (DISC: 后续可在 AdminScope `signOut` / `loginSucceeded` 钩子里 wire).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { StoresService, type StoreListItem } from '../../.generated/api-client';
import { apiErrorMessage, asArray, asRecord } from '../services/api';

/** Antd Select `<Option value, label>` 用. */
export interface StoreOption {
  value: number;
  label: string;
  /** is_active=false 的下线店 (priv-role 显式 includeInactive=true 才会见). */
  disabled?: boolean;
  /** 透传原始 raw, 供调用方做扩展显示 (e.g. tag "已下线"). */
  raw: StoreListItem;
}

interface CacheEntry {
  /** 进行中的 promise, 同时间多调用方共享同一份 in-flight. */
  promise: Promise<StoreListItem[]>;
  /** 已 settle 的 items (成功时填). */
  items?: StoreListItem[];
}

/** 模块级 cache: key = `${includeInactive}` (priv-role 切换可见性需独立 cache). */
const cache = new Map<string, CacheEntry>();

/** 清空缓存 (signOut / actor 切换时调用方主动调). */
export function clearStoresCache(): void {
  cache.clear();
}

/**
 * 拉门店清单 (带缓存). 内部用; React hook 包它给组件用.
 *
 * - cache miss → 发 1 次 GET, 存 promise + items;
 * - cache hit (in-flight) → 复用同 promise;
 * - cache hit (settled) → 直接返 items;
 * - error → 不写 cache, 调用方可重试.
 */
type StoreBrandFilter = 'all' | 'yss' | 'qjx';

function storesCacheKey(
  includeInactive: boolean,
  directOnly: boolean,
  brand: StoreBrandFilter,
): string {
  return `${includeInactive}:${directOnly}:${brand}`;
}

async function fetchStoresWithCache(
  includeInactive: boolean,
  directOnly: boolean,
  brand: StoreBrandFilter,
): Promise<StoreListItem[]> {
  const key = storesCacheKey(includeInactive, directOnly, brand);
  const entry = cache.get(key);
  if (entry?.items) {
    return entry.items;
  }
  if (entry?.promise) {
    return entry.promise;
  }

  const promise = (async (): Promise<StoreListItem[]> => {
    const response = await StoresService.listStoresEndpointApiV1StoresGet({
      includeInactive,
      directOnly,
      brand,
    });
    // SuccessResponse[StoreListResponse] 解包: {data: {items, total}, request_id}
    const root = asRecord(response as unknown as Record<string, unknown>);
    const payload = asRecord(root.data ?? root);
    const items = asArray(payload.items) as unknown as StoreListItem[];
    return items;
  })();

  cache.set(key, { promise });
  try {
    const items = await promise;
    cache.set(key, { promise, items });
    return items;
  } catch (err) {
    // 失败 → 删 cache, 下一个调用方可重试
    cache.delete(key);
    throw err;
  }
}

/**
 * 把 StoreListItem 渲染为 Antd Select option.
 *
 * label 格式: `${store_name_cn} (${city_code})`; city_code=NULL (DISC-143 历史店未矫正)
 * 时退化为 `${store_name_cn}`.
 * 软下线店 (is_active=false) 加 ` · 已下线` 提示 + disabled=true 防误选.
 */
export function storeToOption(item: StoreListItem): StoreOption {
  const cityPart = item.city_code ? ` (${item.city_code})` : '';
  const inactiveTag = item.is_active === false ? ' · 已下线' : '';
  return {
    value: item.store_id,
    label: `${item.store_name_cn}${cityPart}${inactiveTag}`,
    disabled: item.is_active === false,
    raw: item,
  };
}

/**
 * Antd Select `showSearch` 的 `filterOption` 实现.
 *
 * 匹配范围: store_name_cn / internal_code (大小写不敏感, 中文 startsWith / contains).
 * Antd `filterOption(input, option)` 返回 true=保留候选.
 */
export function filterStoreOption(
  input: string,
  option: { label?: string; raw?: StoreListItem } | undefined,
): boolean {
  if (!input || !option) return true;
  const q = input.toLowerCase().trim();
  if (!q) return true;
  const label = (option.label ?? '').toLowerCase();
  const internalCode = (option.raw?.internal_code ?? '').toLowerCase();
  return label.includes(q) || internalCode.includes(q);
}

export interface UseStoresOptionsConfig {
  /** 是否含已下线店 (priv-role / super_admin / owner / hq_ops / finance 才有效, 其他角色 backend 静默忽略). */
  includeInactive?: boolean;
  /** 总部只读页面只展示在营直营门店。 */
  directOnly?: boolean;
  /** 直营门店品牌筛选。 */
  brand?: StoreBrandFilter;
  /**
   * false 时不发请求 (2026-07-06): 供页面级调用方按角色/连接态门控 —
   * 未登录或非全权限角色挂载页面时不得富余地拉门店清单 (401 会触发登出级联,
   * 且违反"连接前不调 scoped API"契约). 变 true 后自动补拉.
   */
  enabled?: boolean;
}

export interface UseStoresOptionsReturn {
  /** 原始 StoreListItem[], 供调用方 filter (e.g. 排除已绑门店). */
  stores: StoreListItem[];
  /** Antd Select 直接消费的 options (含 disabled 软下线店). */
  options: StoreOption[];
  loading: boolean;
  error: string | null;
  /** 强制重拉 (清 cache + 重发). 用于 [刷新] 按钮. */
  reload: () => Promise<void>;
}

/**
 * 拉 actor 可见门店列表的 hook (单次 GET, 多调用方复用 cache).
 *
 * 用法:
 * ```tsx
 * const { options, loading, error } = useStoresOptions();
 * <Select
 *   showSearch
 *   loading={loading}
 *   options={options}
 *   filterOption={filterStoreOption}
 *   notFoundContent={loading ? '加载中...' : error ?? '暂无可见门店'}
 * />
 * ```
 */
export function useStoresOptions(config?: UseStoresOptionsConfig): UseStoresOptionsReturn {
  const includeInactive = config?.includeInactive ?? false;
  const directOnly = config?.directOnly ?? false;
  const brand = config?.brand ?? 'all';
  const enabled = config?.enabled ?? true;
  const [stores, setStores] = useState<StoreListItem[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchOnce = useCallback(async (force: boolean): Promise<void> => {
    setLoading(true);
    setError(null);
    if (force) {
      cache.delete(storesCacheKey(includeInactive, directOnly, brand));
    }
    try {
      const items = await fetchStoresWithCache(includeInactive, directOnly, brand);
      setStores(items);
    } catch (err: unknown) {
      setError(apiErrorMessage(err));
      setStores([]);
    } finally {
      setLoading(false);
    }
  }, [brand, directOnly, includeInactive]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void fetchOnce(false);
  }, [enabled, fetchOnce]);

  const reload = useCallback(() => fetchOnce(true), [fetchOnce]);

  // options 在 stores 变化时纯函数派生; useMemo 避免每次 re-render 重算 + 稳定引用
  const options = useMemo(() => stores.map(storeToOption), [stores]);

  return { stores, options, loading, error, reload };
}
